import { useRef, useState } from "react";
import { toast } from "sonner";
import { CircleHelp, FileDown, Trash2, Upload } from "lucide-react";
import { HelpDialog } from "../../components/site/HelpButton";
import { useFetch } from "../../lib/useFetch";
import { api, download, errMsg, fmtDate } from "../../lib/api";

const KINDS = [["proforma", "Proforma"], ["invoice", "Invoice (also used for credit notes)"]];

const Slot = ({ companyId, kind, label, t, reload }) => {
  const ref = useRef();
  const base = `/admin/companies/${companyId}/word-templates/${kind}`;
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(base, fd);
      toast.success(`${label} template uploaded`);
      if (data.unknown_placeholders?.length) toast.warning(`Unknown placeholders (left empty): ${data.unknown_placeholders.join(", ")}`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    ref.current.value = "";
  };
  const remove = async () => {
    if (!window.confirm(`Remove the ${label} template? The standard layout will be used.`)) return;
    await api.delete(base).catch((e) => toast.error(errMsg(e)));
    reload();
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3" data-testid={`word-slot-${kind}`}>
      <p><b>{label}</b><span className="block text-xs text-slate-500" data-testid={`word-status-${kind}`}>{t === undefined ? "Loading…" : t ? `${t.filename} · uploaded ${fmtDate(t.uploaded_at)}` : "No template: the standard layout is used (PDF only)"}</span></p>
      <div className="flex items-center gap-3 text-xs font-semibold">
        <input ref={ref} type="file" accept=".docx" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`word-file-${kind}`} />
        <button onClick={() => ref.current.click()} className="inline-flex items-center gap-1 text-[#00A8B5]" data-testid={`word-upload-${kind}`}><Upload size={14} />{t ? "Replace" : "Upload .docx"}</button>
        {t && <button onClick={() => download(base, `${kind}-template.docx`)} className="inline-flex items-center gap-1 text-slate-600" data-testid={`word-download-${kind}`}><FileDown size={14} />Download</button>}
        {t && <button onClick={remove} className="text-slate-400 hover:text-rose-500" data-testid={`word-delete-${kind}`}><Trash2 size={14} /></button>}
        <button onClick={() => download(`/admin/word-templates/example/${kind}`, `example-${kind}-template.docx`)} className="text-slate-500 underline" data-testid={`word-example-${kind}`}>Example</button>
      </div>
    </div>
  );
};

const Placeholders = ({ testid = "word-placeholders" }) => {
  const { data } = useFetch("/admin/word-templates/placeholders");
  return (
    <details className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testid} open>
      <summary className="cursor-pointer text-sm font-bold text-[#0A2540]">Placeholders to insert in the Word template</summary>
      <p className="mt-2 text-xs text-slate-500">Type them exactly as shown (with braces). The lines table needs one header row, then a row with {"{%tr for l in lines %}"}, a row with the {"{{ l.… }}"} fields and a row with {"{%tr endfor %}"}. Conditional paragraphs: {"{%p if reverse_charge %}"} … {"{%p endif %}"}.</p>
      {(data || []).map((g) => (
        <div key={g.group} className="mt-3">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{g.group}</p>
          <table className="mt-1 w-full table-fixed text-xs"><tbody>{g.items.map((i) => (
            <tr key={i.tag} className="border-t border-slate-100"><td className="w-80 break-words py-1 pr-4 font-mono text-[#0A2540]"><button onClick={() => navigator.clipboard?.writeText(i.tag).then(() => toast.success("Copied"))} title="Copy">{i.tag}</button></td><td className="py-1 text-slate-600">{i.description}</td></tr>
          ))}</tbody></table>
        </div>
      ))}
    </details>
  );
};

const WordHelp = () => {
  const [open, setOpen] = useState(false);
  return (<>
    <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#00A8B5]" data-testid="word-help-button"><CircleHelp size={16} />How to use placeholders</button>
    {open && <HelpDialog title="Word templates for proforma and invoices" onClose={() => setOpen(false)}>
      <div data-testid="word-help-content" className="space-y-3">
        <ol className="list-decimal space-y-1.5 pl-5">
          <li>Download the <b>Example</b> template (or start from your own Word document) and design it freely: fonts, colours, header, footer.</li>
          <li>Where a value must appear, type its placeholder exactly as listed below, braces included, e.g. <code>{"{{ company_name }}"}</code>. Type it in one go, without formatting part of it differently.</li>
          <li>Upload the .docx in the Proforma or Invoice slot. Credit notes use the Invoice template. Unknown placeholders are reported and left empty.</li>
          <li>From then on, documents of this company are generated from your file: the preview before generating, the PDF and the editable Word (<b>DOCX</b>) download in Proforma / Invoices. Without a template the standard layout is used.</li>
        </ol>
        <p className="font-bold text-[#0A2540]">Lines table</p>
        <p>Create a table with: a header row (your column titles); a row whose first cell contains only <code>{"{%tr for l in lines %}"}</code>; one row with the line fields <code>{"{{ l.description }}"}</code>, <code>{"{{ l.qty }}"}</code>, <code>{"{{ l.unit_net }}"}</code>, <code>{"{{ l.tax_rate }}"}</code>, <code>{"{{ l.net }}"}</code>, <code>{"{{ l.tax }}"}</code>, <code>{"{{ l.total }}"}</code>; a row whose first cell contains only <code>{"{%tr endfor %}"}</code>. The middle row is repeated for every line; the two control rows disappear.</p>
        <p className="font-bold text-[#0A2540]">Conditional blocks</p>
        <p>To show a paragraph only in some cases, put <code>{"{%p if reverse_charge %}"}</code> alone in a paragraph before it and <code>{"{%p endif %}"}</code> alone in a paragraph after it. Useful conditions: <code>reverse_charge</code>, <code>is_proforma</code>, <code>is_invoice</code>, <code>is_credit_note</code>, <code>enrolment_number</code>, <code>proforma_number</code>, <code>cancelled_invoice</code>. Inside a single line use <code>{"{% if is_credit_note %}…{% endif %}"}</code>.</p>
        <p className="font-bold text-[#0A2540]">Logo</p>
        <p>Put <code>{"{{ logo }}"}</code> alone in a paragraph: the company logo (PNG/JPG) is inserted there.</p>
        <Placeholders testid="word-help-placeholders" />
      </div>
    </HelpDialog>}
  </>);
};

export const WordTemplates = ({ companyId }) => {
  const { data, reload } = useFetch(`/admin/companies/${companyId}/word-templates`);
  const by = Object.fromEntries((data || []).map((t) => [t.kind, t]));
  return (
    <div className="space-y-3 rounded-xl bg-white/60 p-3" data-testid={`word-templates-${companyId}`}>
      <div className="flex justify-end"><WordHelp /></div>
      <p className="text-xs text-slate-500">Upload a Word (.docx) template for proforma and for invoices. Documents of this company are then generated from it: preview, PDF and editable Word (.docx); without a template the standard layout is used.</p>
      {KINDS.map(([k, l]) => <Slot key={k} companyId={companyId} kind={k} label={l} t={data ? by[k] || null : undefined} reload={reload} />)}
      <Placeholders />
    </div>
  );
};
